import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Download, Eye, Loader2, FileUp, FolderInput, Plus, Trash2, Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DocumentsDetailModal = ({
  showDocumentsDetailModal,
  setShowDocumentsDetailModal,
  selectedEmployee,
  documentsDetail,
  activeDocCategory,
  setActiveDocCategory,
  documentCategories,
  isTerminatedEmployee,
  newDocumentDetail,
  setNewDocumentDetail,
  handleAddDocumentDetail,
  handleDeleteDocumentDetail,
  setPreviewDoc,
  setShowPreviewModal,
  hrDocReqTab,
  setHrDocReqTab,
  loadDocumentReminders,
  setShowReminderModal,
  documentReminders,
  deleteReminder
}) => {
  if (!showDocumentsDetailModal) return null;

  return (
    <Dialog open={showDocumentsDetailModal} onOpenChange={setShowDocumentsDetailModal}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="documents-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Evraklar - {selectedEmployee?.name}
          </DialogTitle>
        </DialogHeader>
        
        {/* Kategori Seçimi */}
        <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded-lg">
          {documentCategories.map(cat => (
            <Button
              key={cat.key}
              variant={activeDocCategory === cat.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveDocCategory(cat.key)}
              className={activeDocCategory === cat.key ? 'bg-purple-600 hover:bg-purple-700' : ''}
            >
              {cat.label}
              {documentsDetail[cat.key]?.length > 0 && (
                <span className="ml-2 bg-white text-purple-600 rounded-full px-2 py-0.5 text-xs">
                  {documentsDetail[cat.key].length}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Yeni Belge Ekleme - Kesintiler, Fazla Mesai ve İK Belge Talepleri dışındaki kategoriler için */}
        {!isTerminatedEmployee && activeDocCategory !== 'kesintiler' && activeDocCategory !== 'fazla_mesai' && activeDocCategory !== 'ik_belge_talepleri' ? (
          <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800">
              Yeni {documentCategories.find(c => c.key === activeDocCategory)?.label} Ekle
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Belge Adı</Label>
                <Input 
                  value={newDocumentDetail.name} 
                  onChange={(e) => setNewDocumentDetail({...newDocumentDetail, name: e.target.value})}
                  placeholder="Belge adı girin"
                />
              </div>
              <div>
                <Label>Dosya</Label>
                <Input 
                  type="file" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setNewDocumentDetail({...newDocumentDetail, file_url: reader.result});
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
            </div>
            <Button onClick={handleAddDocumentDetail} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="h-4 w-4 mr-2" /> Belge Ekle
            </Button>
          </div>
        ) : activeDocCategory === 'ik_belge_talepleri' ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-amber-50 p-4 rounded-lg border border-amber-200">
              <div>
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <Bell className="h-4 w-4" /> Belge Hatırlatıcıları / Alarmları
                </h4>
                <p className="text-sm text-amber-700">İş sağlığı, ehliyet veya sertifika gibi süreli belgeler için hatırlatıcı kurabilirsiniz.</p>
              </div>
              <Button onClick={() => setShowReminderModal(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                <Plus className="h-4 w-4 mr-2" /> Hatırlatıcı Ekle
              </Button>
            </div>
            
            {documentReminders.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-gray-700">Aktif Hatırlatıcılar</h5>
                {documentReminders.map(rem => {
                  const isOverdue = new Date(rem.due_date) < new Date();
                  const isNear = !isOverdue && new Date(rem.due_date) < new Date(new Date().setDate(new Date().getDate() + 15));
                  
                  return (
                    <div key={rem.id} className={`flex items-center justify-between p-3 border rounded-lg ${isOverdue ? 'bg-red-50 border-red-200' : isNear ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          <Bell className={`h-3 w-3 ${isOverdue ? 'text-red-500' : isNear ? 'text-amber-500' : 'text-gray-400'}`} />
                          {rem.document_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Bitiş Tarihi: <span className={isOverdue ? 'text-red-600 font-bold' : isNear ? 'text-amber-600 font-bold' : ''}>{new Date(rem.due_date).toLocaleDateString('tr-TR')}</span>
                        </p>
                        {rem.notes && <p className="text-xs text-gray-400 mt-1">{rem.notes}</p>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteReminder(rem.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            
            <hr className="my-4 border-gray-200" />
            <h4 className="font-semibold text-gray-800">Çalışandan Talep Edilen Belgeler</h4>
            {documentsDetail[activeDocCategory]?.length === 0 && (
              <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 rounded">Henüz cevap bekleyen veya tamamlanmış talep yok</p>
            )}
            
            {/* Belgeleri isme göre grupla */}
            {documentsDetail[activeDocCategory]?.length > 0 && (
              <div className="space-y-4">
                <div className="flex gap-2 border-b overflow-x-auto pb-2">
                  {/* Tüm benzersiz talep isimlerini bul */}
                  {Array.from(new Set(documentsDetail[activeDocCategory].map(d => d.title))).map((title, idx) => {
                    const hasPending = documentsDetail[activeDocCategory].some(d => d.title === title && d.status === 'pending');
                    return (
                      <button
                        key={idx}
                        onClick={() => setHrDocReqTab(title)}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
                          hrDocReqTab === title 
                            ? 'bg-purple-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {title}
                        {hasPending && <span className="ml-1.5 w-2 h-2 rounded-full bg-amber-500 inline-block"></span>}
                      </button>
                    );
                  })}
                </div>
                
                {/* Seçili isme ait belgeler */}
                <div className="space-y-3">
                  {documentsDetail[activeDocCategory]
                    .filter(d => !hrDocReqTab || d.title === hrDocReqTab)
                    .map(d => (
                    <div key={d.id} className="p-4 bg-white border rounded-lg hover:shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-800">{d.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{d.description}</p>
                          <p className="text-xs text-gray-400 mt-1">Talep Tarihi: {new Date(d.created_at).toLocaleDateString('tr-TR')} {new Date(d.created_at).toLocaleTimeString('tr-TR', {hour: '2-digit', minute:'2-digit'})}</p>
                        </div>
                        <Badge variant={d.status === 'completed' ? 'success' : 'warning'} className={d.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {d.status === 'completed' ? 'Tamamlandı' : 'Cevap Bekliyor'}
                        </Badge>
                      </div>
                      
                      {d.status === 'completed' && d.responses && d.responses.length > 0 && (
                        <div className="mt-4 pt-3 border-t">
                          <p className="text-sm font-medium text-gray-700 mb-2">Çalışanın Gönderdiği Belgeler:</p>
                          <div className="space-y-2">
                            {d.responses.map((resp, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-gray-50 p-2 rounded text-sm">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-500" />
                                  <span className="truncate max-w-[200px]">{resp.file_name}</span>
                                  <span className="text-xs text-gray-400">({new Date(resp.submitted_at).toLocaleDateString('tr-TR')})</span>
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={() => {
                                      setPreviewDoc({ file_url: resp.file_url, file_name: resp.file_name });
                                      setShowPreviewModal(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = resp.file_url;
                                      link.download = resp.file_name;
                                      link.click();
                                    }}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Evrak Listesi */}
        {activeDocCategory !== 'ik_belge_talepleri' && (
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">{documentCategories.find(c => c.key === activeDocCategory)?.label} Listesi</h4>
            {(!documentsDetail[activeDocCategory] || documentsDetail[activeDocCategory].length === 0) ? (
              <p className="text-sm text-gray-500 p-4 text-center bg-gray-50 rounded">Bu kategoride henüz belge bulunmuyor</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                {documentsDetail[activeDocCategory].map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className={`h-5 w-5 ${activeDocCategory === 'kesintiler' ? 'text-red-500' : activeDocCategory === 'fazla_mesai' ? 'text-orange-500' : 'text-purple-600'}`} />
                      <div>
                        <p className="font-medium text-sm">{doc.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {activeDocCategory === 'kesintiler' ? (
                            <>
                              <Badge variant="outline" className="text-red-600 bg-red-50">{doc.deduction_type}</Badge>
                              <span>{doc.month}/{doc.year} - {doc.amount} TL</span>
                            </>
                          ) : activeDocCategory === 'fazla_mesai' ? (
                            <>
                              <span>{doc.shift_date}</span>
                              <span>{doc.hours} Saat</span>
                              <Badge variant="outline" className="text-orange-600 bg-orange-50">{doc.type === 'weekend' ? 'Hafta Sonu' : 'Hafta İçi'}</Badge>
                            </>
                          ) : (
                            <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {doc.file_url && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              setPreviewDoc({ file_url: doc.file_url, file_name: doc.name });
                              setShowPreviewModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.file_url;
                              link.download = doc.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {!isTerminatedEmployee && activeDocCategory !== 'kesintiler' && activeDocCategory !== 'fazla_mesai' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteDocumentDetail(doc.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentsDetailModal;
